import { useState } from "react";
import { toast } from "sonner";
import { BrainCircuit, ShieldBan, Crown } from "lucide-react";
import { usePoll } from "@/lib/usePoll";
import { api, ago, errMsg, usd } from "@/lib/api";
import { Panel, Badge, Empty, PageTitle } from "@/components/common";
import { Button } from "@/components/ui/button";

function Conf({ v }) {
  if (v === null || v === undefined) return <span className="font-mono text-xs text-zinc-500">n/a</span>;
  const pct = Math.round(v * 100);
  return (
    <div className="flex items-center gap-2 w-28">
      <div className="h-1.5 flex-1 bg-zinc-800 rounded"><div className="h-full rounded bg-[#F7931A]" style={{ width: `${pct}%` }} /></div>
      <span className="font-mono text-xs text-zinc-300">{pct}%</span>
    </div>
  );
}

function Evidence({ items, tone }) {
  if (!items?.length) return <div className="text-xs text-zinc-600">—</div>;
  return <ul className="space-y-1">{items.filter(Boolean).map((x, i) => <li key={i} className={`text-xs leading-snug ${tone === "for" ? "text-emerald-300/80" : "text-red-300/80"}`}>{tone === "for" ? "+" : "−"} {String(x)}</li>)}</ul>;
}

function AgentCard({ meta, r }) {
  return (
    <div data-testid={`agent-card-${meta.id}`} className="panel p-4 flex flex-col gap-3 fade-up">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] text-zinc-500">AGENT {meta.n}</div>
          <div className="font-display font-semibold text-zinc-100">{meta.name}</div>
          <div className="font-mono text-[10px] text-zinc-600 mt-0.5">{meta.model}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge v={r?.stance} testid={`agent-stance-${meta.id}`} />
          <Badge v={r?.status || "neutral"}>{r?.status || "idle"}</Badge>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Conf v={r?.confidence} />
        <span className="font-mono text-[10px] text-zinc-500" data-testid={`agent-freshness-${meta.id}`}>{r ? `updated ${ago(r.ts)}` : "no data"}</span>
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed">{r?.summary || "Waiting for the first analysis cycle."}</p>
      {r && <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-800/70">
        <div><div className="font-mono text-[10px] text-zinc-500 mb-1">SUPPORTING</div><Evidence items={r.supporting} tone="for" /></div>
        <div><div className="font-mono text-[10px] text-zinc-500 mb-1">OPPOSING</div><Evidence items={r.opposing} tone="against" /></div>
      </div>}
    </div>
  );
}

function RiskCard({ r }) {
  return (
    <Panel title="Agent 7 · Risk Management (absolute veto)" icon={ShieldBan} testid="agent-card-risk">
      {!r ? <Empty>No risk evaluation yet.</Empty> : (
        <div className="grid md:grid-cols-2 gap-4">
          {["long", "short"].map((k) => {
            const p = r.plans?.[k] || {};
            return (
              <div key={k} data-testid={`risk-plan-${k}`} className={`rounded-md border p-3 ${p.approved ? "border-emerald-700/50" : "border-red-800/60 bg-red-950/10"}`}>
                <div className="flex justify-between items-center mb-2"><span className="font-mono text-xs uppercase text-zinc-300">{k} · {p.product}</span><Badge v={p.approved ? "approve" : "veto"}>{p.approved ? "approved" : "veto"}</Badge></div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[11px] text-zinc-400">
                  <span>Entry {p.entry ?? "—"}</span><span>Size {p.volume_btc ?? "—"} BTC</span>
                  <span>Stop {p.stop_loss ?? "—"}</span><span>Target {p.take_profit ?? "—"}</span>
                  <span>Max loss {usd(p.max_loss_usd, 4)}</span><span>Net R/R {p.net_rr ?? "—"}</span>
                  <span>Fees {usd(p.fees_usd, 4)}</span><span>Slippage {usd(p.slippage_usd, 4)}</span>
                  <span>Leverage {p.leverage ?? "—"}x</span><span>Liq. {p.liquidation_price_est ?? "n/a"}</span>
                  <span>Funding 24h {usd(p.funding_usd_24h, 4)}</span><span>Capital {usd(p.capital_used, 2)}</span>
                  {(p.mode === "flip" || p.mode === "compound") && <><span className="text-[#F7931A]">{p.mode === "flip" ? `Needed move ${p.tp_move_pct}%` : `Win ≈ ${usd(p.expected_reward_usd, 2)}`}</span><span>{p.stop_pct ? `Stop ${p.stop_pct}% · ` : ""}breakeven p {p.breakeven_probability}</span></>}
                </div>
                {p.vetoes?.length > 0 && <ul className="mt-2 space-y-0.5">{p.vetoes.map((v, i) => <li key={i} className="text-[11px] text-red-300/90">✗ {v}</li>)}</ul>}
              </div>
            );
          })}
        </div>)}
    </Panel>
  );
}

function ChiefCard({ r, d }) {
  const raw = r?.raw || {};
  return (
    <Panel title="Agent 8 · Chief Decision AI" icon={Crown} testid="agent-card-chief" className="border-[#F7931A]/30">
      {!r ? <Empty>No decision yet.</Empty> : (
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
          <div>
            <div className="font-mono text-[10px] text-zinc-500">FINAL DECISION (after selectivity gate)</div>
            <div data-testid="chief-final-decision" className={`font-display text-4xl font-extrabold mt-1 ${d?.final_decision === "BUY" ? "text-emerald-400" : d?.final_decision === "SHORT" ? "text-red-400" : "text-amber-400"}`}>{(d?.final_decision || "NO_TRADE").replace("_", " ")}</div>
            <div className="font-mono text-xs text-zinc-500 mt-2">Chief proposed: {raw.decision} · probability {raw.probability ?? "—"} · agreement {d?.gate?.agreement ?? "—"}/6</div>
            <p className="text-sm text-zinc-400 mt-3">{r.summary}</p>
            {d?.calibration && <div className="font-mono text-[10px] text-zinc-500 mt-3">Calibration: {d.calibration.note || `OOS win rate ${d.calibration.oos_overall_win_rate ?? "—"} · live trades ${d.calibration.live_closed_trades}`}</div>}
          </div>
          <div className="space-y-3">
            <div><div className="font-mono text-[10px] text-red-400 mb-1">STRONGEST ARGUMENT AGAINST ENTERING</div><p className="text-sm text-zinc-300">{raw.strongest_counterargument || "—"}</p></div>
            <div><div className="font-mono text-[10px] text-zinc-500 mb-1">CONFLICTS</div><Evidence items={raw.conflicts} tone="against" /></div>
            <div><div className="font-mono text-[10px] text-zinc-500 mb-1">ASSUMPTIONS CHALLENGED</div><Evidence items={raw.assumptions_challenged} tone="against" /></div>
            {raw.follow_up_round?.length > 0 && <div><div className="font-mono text-[10px] text-zinc-500 mb-1">FOLLOW-UP ANALYSIS REQUESTED</div>{raw.follow_up_round.map((f, i) => <div key={i} className="text-xs text-zinc-400">→ {f.agent}: {f.question}</div>)}</div>}
            {d?.gate?.blocks?.length > 0 && <div data-testid="gate-blocks"><div className="font-mono text-[10px] text-amber-400 mb-1">SELECTIVITY GATE BLOCKS</div>{d.gate.blocks.map((b, i) => <div key={i} className="text-xs text-amber-300/80">▸ {b}</div>)}</div>}
          </div>
        </div>)}
    </Panel>
  );
}

export default function CommandCenter() {
  const { data, reload } = usePoll("/agents", 5000);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/agents/run");
      toast(r.data.started ? "Analysis cycle started" : r.data.reason);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const reps = data?.reports || {};
  const prog = data?.progress;
  return (
    <div>
      <PageTitle title="AI Command Center" sub="Six independent analysts, a deterministic risk engine with absolute veto, and a Chief Decision AI. NO TRADE is the default outcome."
        right={<div className="flex items-center gap-3">{prog?.running && <span data-testid="cycle-progress" className="font-mono text-xs text-[#F7931A] live-dot">● {prog.stage}</span>}
          <Button data-testid="agents-run-button" onClick={run} disabled={busy || prog?.running} className="bg-[#F7931A] hover:bg-[#E08213] text-black font-semibold"><BrainCircuit className="w-4 h-4 mr-2" />Run analysis cycle</Button></div>} />
      <div className="space-y-4">
        <ChiefCard r={reps.chief} d={data?.decision} />
        <RiskCard r={reps.risk} />
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(data?.agents || []).filter((a) => a.n <= 6).map((a) => <AgentCard key={a.id} meta={a} r={reps[a.id]} />)}
        </div>
      </div>
    </div>
  );
}
